import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useRouter } from 'expo-router';
import { ArrowLeft } from 'lucide-react-native';
import { useEffect } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { PremiumFooter } from '../../src/components/landing/PremiumFooter';
import { Container } from '../../src/components/primitives/Container';
import { Text } from '../../src/components/primitives/Text';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { trackEvent } from '../../src/lib/analytics';
import type { ChangelogEntry, ChangelogFeedResponse } from '../../src/lib/api';
import { changelogAPI } from '../../src/lib/api';
import { useMixpanel } from '../../src/lib/mixpanel';
import { colors } from '../../src/theme/colors';
import { spacing } from '../../src/theme/spacing';

// ---------------------------------------------------------------------------
// Web-only route: /changelog
// Renders the public feed of published changelog entries.
// ---------------------------------------------------------------------------

function TagBadge({ label }: { label: string }) {
  return (
    <View style={styles.tag}>
      <Text variant="caption" color={colors.primary[500]} style={styles.tagText}>
        {label}
      </Text>
    </View>
  );
}

interface EntryCardProps {
  entry: ChangelogEntry;
  onPress: () => void;
}

function EntryCard({ entry, onPress }: EntryCardProps) {
  const { t } = useTranslation();
  const dateLabel = entry.publishedAt ? format(new Date(entry.publishedAt), 'MMM d, yyyy') : null;

  return (
    <Pressable
      testID={`changelog-entry-${entry.id}`}
      onPress={onPress}
      style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
      accessibilityRole="button"
      accessibilityLabel={entry.title}
    >
      <View style={styles.cardMeta}>
        {entry.version ? (
          <View testID={`changelog-version-${entry.id}`} style={styles.versionBadge}>
            <Text variant="caption" color={colors.primary[600]} style={styles.versionText}>
              {t('changelog.version')}
              {entry.version}
            </Text>
          </View>
        ) : null}
        {dateLabel ? (
          <Text variant="caption" color={colors.text.tertiary}>
            {dateLabel}
          </Text>
        ) : null}
      </View>

      <Text variant="h4" style={styles.cardTitle}>
        {entry.title}
      </Text>

      {entry.tags.length > 0 ? (
        <View style={styles.tagsRow}>
          {entry.tags.map((tag) => (
            <TagBadge key={tag} label={tag} />
          ))}
        </View>
      ) : null}

      <View style={styles.cardDivider} />
    </Pressable>
  );
}

export default function ChangelogListPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const mp = useMixpanel();

  const { data, isLoading, isError } = useQuery<ChangelogFeedResponse>({
    queryKey: ['changelog-feed'],
    queryFn: async () => {
      const response = await changelogAPI.getFeed({ limit: 20, offset: 0 });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data && !isLoading) {
      trackEvent(mp, 'changelog.feed_viewed', { entry_count: data.data.length });
    }
  }, [data]);

  const entries = data?.data ?? [];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Container maxWidth="md">
        <Pressable
          onPress={() => router.push('/landing')}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel={t('changelog.backToHome')}
        >
          <ArrowLeft size={20} color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.primary[500]}>
            {t('changelog.backToHome')}
          </Text>
        </Pressable>

        <View style={styles.header}>
          <Text variant="h1" style={styles.title} accessibilityRole="header">
            {t('changelog.title')}
          </Text>
          <Text variant="body" color={colors.text.secondary}>
            {t('changelog.subtitle')}
          </Text>
        </View>

        <View style={styles.divider} />

        {isLoading ? (
          <View testID="changelog-loading" style={styles.stateContainer}>
            <Text variant="body" color={colors.text.secondary}>
              {t('changelog.loading')}
            </Text>
          </View>
        ) : isError ? (
          <View testID="changelog-error" style={styles.stateContainer}>
            <Text variant="body" color={colors.status.error.main}>
              {t('changelog.error')}
            </Text>
          </View>
        ) : entries.length === 0 ? (
          <View testID="changelog-empty" style={styles.stateContainer}>
            <Text variant="body" color={colors.text.secondary}>
              {t('changelog.empty')}
            </Text>
          </View>
        ) : (
          <View testID="changelog-list">
            {entries.map((entry) => (
              <EntryCard
                key={entry.id}
                entry={entry}
                onPress={() => router.push(`/changelog/${entry.slug}` as any)}
              />
            ))}
          </View>
        )}
      </Container>

      <PremiumFooter />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  content: {
    flexGrow: 1,
    paddingTop: Platform.select({ web: 60, default: 40 }),
    paddingBottom: spacing.xxl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xl,
    alignSelf: 'flex-start',
  },
  header: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    fontSize: Platform.select({ web: 42, default: 32 }),
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
  stateContainer: {
    paddingVertical: spacing.xxl,
    alignItems: 'center',
  },
  card: {
    paddingVertical: spacing.lg,
    paddingHorizontal: 0,
  },
  cardPressed: {
    opacity: 0.7,
  },
  cardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  versionBadge: {
    backgroundColor: colors.primary[50],
    borderRadius: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: colors.primary[200],
  },
  versionText: {
    fontWeight: '600',
  },
  cardTitle: {
    fontWeight: '700',
    marginBottom: spacing.sm,
    color: colors.text.primary,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  tag: {
    backgroundColor: colors.background.secondary,
    borderRadius: 4,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  tagText: {
    fontWeight: '500',
  },
  cardDivider: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginTop: spacing.lg,
  },
});
